import React, { useEffect, useState } from 'react';
import caseService from '../../services/caseService';

const CaseDashboard = ({ onCaseSelect }) => {
  const [stats, setStats] = useState(null);
  const [billingStats, setBillingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [dashStats, billStats] = await Promise.all([
        caseService.getDashboardStats(),
        caseService.getBillingStats().catch(() => null), // May fail for non-admin
      ]);
      setStats(dashStats);
      setBillingStats(billStats);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 backdrop-blur-sm">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Cases', value: stats?.total || 0, gradient: 'from-cyan-500/20 to-cyan-600/20', border: 'border-cyan-500/30', textColor: 'text-cyan-300', icon: '📁' },
    { label: 'Active', value: stats?.active || 0, gradient: 'from-green-500/20 to-green-600/20', border: 'border-green-500/30', textColor: 'text-green-300', icon: '✓' },
    { label: 'Pending', value: stats?.pending || 0, gradient: 'from-amber-500/20 to-amber-600/20', border: 'border-amber-500/30', textColor: 'text-amber-300', icon: '⏳' },
    { label: 'Closed', value: stats?.closed || 0, gradient: 'from-slate-500/20 to-slate-600/20', border: 'border-slate-500/30', textColor: 'text-slate-300', icon: '🔒' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className={`bg-gradient-to-br ${stat.gradient} border ${stat.border} rounded-xl p-6 backdrop-blur-sm hover:scale-[1.02] transition-transform`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${stat.textColor}`}>{stat.label}</p>
                <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className="text-4xl opacity-80">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Billing Stats (if available) */}
      {billingStats && (
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4">💰 Billing Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-virgo-navy-800/50 rounded-lg p-4">
              <p className="text-sm text-virgo-teal-400">Unbilled Hours</p>
              <p className="text-2xl font-bold text-white mt-1">
                {billingStats?.unbilled_hours?.toFixed(1) || '0.0'} hrs
              </p>
            </div>
            <div className="bg-virgo-navy-800/50 rounded-lg p-4">
              <p className="text-sm text-virgo-teal-400">Unbilled Amount</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                ${billingStats?.unbilled_amount?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="bg-virgo-navy-800/50 rounded-lg p-4">
              <p className="text-sm text-virgo-teal-400">Cases with Unbilled Time</p>
              <p className="text-2xl font-bold text-white mt-1">
                {billingStats?.cases_with_unbilled_time || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Practice Area Breakdown */}
      {stats?.by_practice_area && Object.keys(stats.by_practice_area).length > 0 && (
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/20 p-6 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4">📊 Cases by Practice Area</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_practice_area)
              .sort((a, b) => b[1] - a[1])
              .map(([area, count]) => (
                <div key={area} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-virgo-teal-300">{area || 'Unassigned'}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-48 bg-virgo-navy-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.total) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-white w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Attorney Breakdown */}
      {stats?.by_assigned_attorney && Object.keys(stats.by_assigned_attorney).length > 0 && (
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-green-500/20 p-6 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4">👥 Cases by Attorney</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_assigned_attorney)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([attorney, count]) => (
                <div key={attorney} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-virgo-teal-300">{attorney}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-virgo-navy-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.total) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-white w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-6 backdrop-blur-sm">
        <h3 className="text-lg font-semibold text-blue-300 mb-4">📈 Recent Activity</h3>
        <p className="text-3xl font-bold text-white">
          {stats?.recent_activity_count || 0}
        </p>
        <p className="text-sm text-blue-300/70 mt-1">Updates in the last 7 days</p>
      </div>
    </div>
  );
};

export default CaseDashboard;
